import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  HelpCircle, 
  Send, 
  X, 
  Loader2, 
  MessageSquare,
  Calendar,
  Clock,
  User,
  RefreshCw,
  CheckCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "America/Phoenix", label: "Arizona (Phoenix)" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Central Europe (Paris)" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Asia/Tokyo", label: "Japan (Tokyo)" },
  { value: "Asia/Shanghai", label: "China (Shanghai)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Dubai", label: "Dubai (UAE)" },
  { value: "Asia/Kolkata", label: "India (Mumbai)" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Australia/Melbourne", label: "Melbourne" },
  { value: "America/Sao_Paulo", label: "Brazil (São Paulo)" },
  { value: "America/Mexico_City", label: "Mexico City" },
];

// Get user's timezone
function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/New_York";
  }
}

// Chat Message Component
function ChatMessage({ message, isUser }) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div 
        className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
          isUser 
            ? 'bg-primary text-primary-foreground' 
            : 'bg-muted'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        {message.timestamp && (
          <p className={`text-[10px] mt-1 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}

// Slot Selection Component
function SlotSelector({ slots, onSelect, loading }) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No available slots found. Please try different preferences.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {slots.map(slot => (
        <Button
          key={slot.id}
          variant="outline"
          className="w-full justify-between h-auto py-3 px-4"
          onClick={() => onSelect(slot)}
        >
          <div className="flex flex-col items-start">
            <span className="font-medium">{slot.dayOfWeek}, {slot.date}</span>
            <span className="text-sm text-muted-foreground">{slot.timeRange}</span>
          </div>
          <ChevronRight className="h-4 w-4" />
        </Button>
      ))}
    </div>
  );
}

// Main AI Support Chat Component
export default function AISupportChat({ 
  trigger, 
  triggerClassName = "",
  showFAB = false 
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  
  // Escalation state
  const [showScheduler, setShowScheduler] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(getUserTimezone());
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [escalationData, setEscalationData] = useState({
    task: '',
    problem: '',
    urgency: ''
  });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const { user } = useAuth();
  const { t, language } = useTranslation();

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Get current context
  const getContext = () => {
    const path = location.pathname;
    let page = 'dashboard';
    let dealId = null;
    let documentId = null;

    if (path.startsWith('/deal/')) {
      page = 'deal';
      dealId = path.split('/deal/')[1]?.split('/')[0];
    } else if (path.startsWith('/documents')) {
      page = 'documents';
    } else if (path.startsWith('/settings')) {
      page = 'settings';
    } else if (path.startsWith('/offices') || path.startsWith('/virtual-offices')) {
      page = 'virtual-offices';
    } else if (path.startsWith('/clients')) {
      page = 'clients';
    } else if (path.startsWith('/deals')) {
      page = 'deals';
    }

    return { page, dealId, documentId, language };
  };

  // Send message to AI
  const sendMessage = async (messageText = inputValue.trim()) => {
    if (!messageText) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await api.post('/support/chat', {
        message: messageText,
        conversationId,
        context: getContext()
      });

      setConversationId(response.conversationId);

      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Check for escalation suggestion
      if (response.suggestEscalation) {
        // AI might suggest scheduling - we'll detect this and offer the scheduler
      }
    } catch (error) {
      toast.error(t('support.error') || 'Failed to send message. Please try again.');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
      setInputValue(messageText);
    } finally {
      setLoading(false);
    }
  };

  // Load available slots
  const loadSlots = async () => {
    setSlotsLoading(true);
    try {
      const response = await api.get('/support/slots', {
        params: { userTimezone: selectedTimezone }
      });
      setSlots(response.slots || []);
    } catch (error) {
      toast.error(t('support.slotsError') || 'Failed to load available times.');
    } finally {
      setSlotsLoading(false);
    }
  };

  // Open scheduler
  const handleScheduleCall = () => {
    setShowScheduler(true);
    loadSlots();
  };

  // Book a slot
  const handleBookSlot = async (slot) => {
    try {
      const response = await api.post('/support/book', {
        slotId: slot.id,
        userTimezone: selectedTimezone,
        summary: {
          ...escalationData,
          conversationId,
          userEmail: user?.email,
          userName: user?.name
        }
      });

      setBookingConfirmed(true);
      toast.success(response.message || t('support.booked') || 'Call scheduled successfully!');

      // Add confirmation message to chat
      const confirmMessage = {
        role: 'assistant',
        content: t('support.bookingConfirmed', { time: slot.startTimeLocal }) || 
          `Great, I've scheduled your call for ${slot.startTimeLocal}. You'll receive a confirmation with the call link. Is there anything else I can help with?`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, confirmMessage]);
      setShowScheduler(false);
    } catch (error) {
      toast.error(error.message || t('support.bookingError') || 'Failed to book slot. Please try again.');
    }
  };

  // Clear conversation
  const handleClearChat = async () => {
    if (conversationId) {
      try {
        await api.delete(`/support/conversation/${conversationId}`);
      } catch (e) {
        // Ignore errors on clear
      }
    }
    setMessages([]);
    setConversationId(null);
    setEscalationData({ task: '', problem: '', urgency: '' });
    setBookingConfirmed(false);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Welcome message
  const welcomeMessage = {
    role: 'assistant',
    content: t('support.welcome') || "Hi! I'm the PocketFiler AI assistant. I can help you with:\n\n- Creating and managing deals\n- Sending documents for signature\n- Using the QR connect feature\n- Inviting clients\n- Any other questions about PocketFiler\n\nHow can I help you today?",
    timestamp: new Date().toISOString()
  };

  // Chat content
  const chatContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <MessageSquare className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">{t('support.title') || 'AI Support'}</h3>
            <p className="text-xs text-muted-foreground">{t('support.subtitle') || 'Ask anything about PocketFiler'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClearChat}
              className="h-8 w-8"
              title={t('support.clearChat') || 'Clear chat'}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen(false)}
            className="h-8 w-8 md:hidden"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <ChatMessage message={welcomeMessage} isUser={false} />
        ) : (
          messages.map((msg, idx) => (
            <ChatMessage key={idx} message={msg} isUser={msg.role === 'user'} />
          ))
        )}
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="bg-muted rounded-lg px-4 py-2.5">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </ScrollArea>

      {/* Quick Actions */}
      {messages.length > 2 && !showScheduler && !bookingConfirmed && (
        <div className="px-4 pb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleScheduleCall}
            className="w-full gap-2"
          >
            <Calendar className="h-4 w-4" />
            {t('support.scheduleCall') || 'Schedule a call with support'}
          </Button>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('support.inputPlaceholder') || 'Type your question...'}
            disabled={loading}
            className="flex-1 min-h-[44px]"
            data-testid="support-chat-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={loading || !inputValue.trim()}
            size="icon"
            className="min-h-[44px] min-w-[44px]"
            data-testid="support-chat-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Scheduler dialog content
  const schedulerContent = (
    <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('support.scheduleTitle') || 'Schedule a Support Call'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Timezone selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('support.yourTimezone') || 'Your timezone'}
            </label>
            <Select
              value={selectedTimezone}
              onValueChange={(value) => {
                setSelectedTimezone(value);
                loadSlots();
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Available slots */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('support.availableTimes') || 'Available times'} 
              <span className="text-muted-foreground font-normal ml-1">
                ({t('support.inYourTime') || 'in your local time'})
              </span>
            </label>
            <SlotSelector 
              slots={slots} 
              onSelect={handleBookSlot}
              loading={slotsLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowScheduler(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Desktop: Sheet from right side
  // Mobile: Full screen overlay via same Sheet
  return (
    <>
      {/* Desktop trigger (header button) */}
      {trigger ? (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            {trigger}
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:w-[400px] p-0">
            {chatContent}
          </SheetContent>
        </Sheet>
      ) : null}

      {/* Mobile FAB */}
      {showFAB && (
        <>
          <Button
            onClick={() => setOpen(true)}
            className="fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg z-40 md:hidden"
            size="icon"
            data-testid="support-fab"
            aria-label={t('support.openChat') || 'Open AI support'}
          >
            <HelpCircle className="h-6 w-6" />
          </Button>

          {/* Mobile full-screen chat */}
          {open && (
            <div className="fixed inset-0 z-50 bg-background md:hidden">
              {chatContent}
            </div>
          )}
        </>
      )}

      {/* Scheduler dialog */}
      {schedulerContent}
    </>
  );
}

// Header button component for easy integration
export function AISupportButton() {
  const { t } = useTranslation();
  
  return (
    <AISupportChat
      trigger={
        <Button
          variant="outline"
          size="sm"
          className="gap-2 min-h-[40px]"
          data-testid="support-button"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="hidden md:inline">{t('support.askAI') || 'Ask AI'}</span>
        </Button>
      }
    />
  );
}
